// Schritt 7: alles auf einer Seite, offene Punkte mit Link in ihren Schritt,
// die `.env` als Vorschau (geschwärzt), dann „Mandant anlegen".

import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { get, post } from "../../api/client";
import { keys, useCatalog } from "../../api/queries";
import type { Catalog, Checks, Tenant } from "../../api/types";
import { checkText, errorText, WIZARD_STEPS } from "../../lib/labels";
import { Link, useNavigate } from "../../router/router";
import { Badge, Note } from "../../ui/badge";
import { BusyButton } from "../../ui/busy-button";
import { Card } from "../../ui/card";
import { toast } from "../../ui/toaster";
import { StepsNav } from "./steps-nav";
import { WizardHead } from "./wizard-head";

/** Die Zeilen der Zusammenfassung — rein, damit der Test sie prüfen kann. */
export function summaryRows(t: Tenant, catalog: Catalog): Array<[string, ReactNode]> {
  const l = t.license;
  const m = t.models;
  const ints = catalog.integrations.filter((i) => t.integrations[i.key]).map((i) => i.name);
  return [
    ["Kunde", <>{t.name} · <code>{t.host}</code>{t.contact_email ? ` · ${t.contact_name} <${t.contact_email}>` : ""}</>],
    ["Lizenz", `${l.max_users ?? "∞"} Benutzer, ${l.max_workspaces ?? "∞"} Arbeitsbereiche, Features: ${l.features ? l.features.join(", ") : "alle"}, bis ${l.valid_until ?? "unbefristet"}`],
    ["Anmeldung", t.auth.modes.join(", ")],
    ["Modelle", [m.platform.enabled ? `Plattform-Key (${m.platform.budget_usd} USD/Monat)` : "", m.own.enabled ? `eigener Endpoint ${m.own.name}` : ""].filter(Boolean).join(" + ") + ` · Standard: ${m.default === "own" ? "eigener" : "Plattform"}`],
    ["Flow-Modelle", m.flows.enabled
      ? `${m.flows.models.map((x) => x.name).join(", ") || "keine"} · eigener Key (${m.flows.budget_usd} USD/Monat)`
      : "aus"],
    ["Integrationen", ints.length ? ints.join(", ") : "keine"],
    ["Skills ohne Integration", t.skills.join(", ") || "keine"],
    ["Erster Tenant-Admin", t.first_admin?.email ?? "—"],
    // Den Build wählt niemand: Die Einrichtung nimmt den neuesten aus der Registry.
    ["Build", t.build ? <code>{t.build}</code> : "der neueste beim Einrichten"],
  ];
}

export function Summary({ tenant: t, checks }: { tenant: Tenant; checks: Checks }) {
  const catalog = useCatalog();
  const navigate = useNavigate();
  const client = useQueryClient();
  // Die Vorschau ist Beiwerk — scheitert sie, fehlt nur ihre Karte.
  const env = useQuery({ queryKey: keys.tenantEnv(t.id), queryFn: async () => (await get<{ env: Record<string, string> }>(`/tenants/${t.id}/env`)).env });
  const problems = Object.entries(checks).flatMap(([n, c]) => Object.entries(c.errors).map(([k, code]) => ({ n, k, code })));
  const warnings = Object.values(checks).flatMap((c) => c.warnings);

  const provision = async () => {
    try {
      await post(`/tenants/${t.id}/provision`);
      void client.invalidateQueries({ queryKey: keys.tenant(t.id) });
      navigate(`/tenants/${t.id}`);
    } catch (err) { toast(errorText(err), "error"); }
  };

  return (
    <>
      <WizardHead tenant={t} />
      <StepsNav tenant={t} current={7} />
      <Card title="7. Zusammenfassung" flush>
        <div className="pf-table-wrap"><table className="pf-table"><tbody>
          {summaryRows(t, catalog).map(([k, v]) => <tr key={k}><th>{k}</th><td>{v}</td></tr>)}
        </tbody></table></div>
      </Card>
      {problems.length > 0 && (
        <Card title="Noch offen">
          <ul className="pf-list">
            {problems.map(({ n, k, code }) => (
              <li key={`${n}-${k}`}><Link to={`/tenants/${t.id}/wizard/${n}`}>{WIZARD_STEPS[Number(n) - 1]}</Link>: <code>{k}</code> — {checkText(code)}</li>
            ))}
          </ul>
        </Card>
      )}
      {warnings.length > 0 && <Card title="Hinweise"><ul className="pf-list">{warnings.map((w, i) => <li key={i}>{checkText(w)}</li>)}</ul></Card>}
      {env.data && (
        <Card title=".env der Instanz (Vorschau, Geheimnisse geschwärzt)" hint="Dazu kommen die Werte, die nur der Provisioner kennt: DB-Passwort, SESSION_SECRET, ENCRYPTION_KEY, MAINTENANCE_TOKEN, Appwrite-Konto.">
          <div className="pf-log">{Object.entries(env.data).map(([k, v]) => `${k}=${v}`).join("\n")}</div>
        </Card>
      )}
      <Card
        title="Mandant anlegen"
        hint="Legt DNS, Datenbank, Appwrite, Requesty-Key, Dateien und Instanz an — Schritt für Schritt, mit Fortschritt. Scheitert ein Schritt, kannst du ab dort wiederholen oder alles zurückbauen."
        foot={<>
          <Link className="pf-btn" to={`/tenants/${t.id}/wizard/6`}>Zurück</Link>
          <BusyButton className="pf-btn pf-btn--primary" disabled={problems.length > 0} onClick={provision}>Mandant anlegen</BusyButton>
        </>}
      >
        {problems.length ? <Note tone="warn">Erst die offenen Punkte oben ergänzen.</Note> : <Note>Bereit <Badge tone="ok">vollständig</Badge></Note>}
      </Card>
    </>
  );
}
